import { FormattedPhone } from "@/components/base/text/formatted-phone";
import { formatDisplayName } from "@/utils/name";
import { Badge } from "@/components/base/badges/badges";
import { PayoutSetupStatusBadge } from "@/components/talent-members/payout-setup-status-badge";
import { IdentityVerificationEntityBadge } from "@/components/talent-members/identity-verification-entity-badge";
import { getTagBadgeColors, resolveTagColor } from "@/utils/tag-colors";
import { Dot } from "@/components/foundations/dot-icon";
import type { TalentMemberDto } from "@fp/admin-api-client/types/talentMemberDto.js";

interface TalentPreviewCardProps {
  talent: TalentMemberDto;
}

const getEmploymentStatusLabel = (type: string | null | undefined): string => {
  switch (type) {
    case "1099":
      return "1099";
    case "w2":
      return "W2";
    default:
      return "Unclassified";
  }
};

export const TalentPreviewCard = ({ talent }: TalentPreviewCardProps) => {
  const hasLocation = talent.address?.city || talent.address?.state;

  return (
    <div className="w-72 rounded-lg bg-white shadow-xl border border-secondary p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-lg font-bold text-primary">
          {formatDisplayName(talent)}
        </p>
        <Badge size="sm" color="blue">
          {getEmploymentStatusLabel(talent.type)}
        </Badge>
      </div>

      <div className="space-y-1">
        {talent.email && (
          <p className="text-sm text-secondary">{talent.email}</p>
        )}
        {talent.phone && (
          <p className="text-sm text-secondary">
            <FormattedPhone value={talent.phone} />
          </p>
        )}
        {hasLocation && (
          <p className="text-sm text-tertiary">
            {[talent.address?.city, talent.address?.state].filter(Boolean).join(", ")}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <PayoutSetupStatusBadge status={talent.payoutSetupStatus} />
        <IdentityVerificationEntityBadge
          entityType={talent.identityVerificationEntityType}
          businessName={talent.identityVerificationBusinessName}
        />
      </div>

      {/* Tags */}
      {talent.tags && talent.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {talent.tags.map((tag) => {
            const { bg, text } = getTagBadgeColors(resolveTagColor(tag.hexColor));
            return (
              <span
                key={tag.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                style={{ backgroundColor: bg, color: text }}
              >
                <Dot size="sm" style={{ color: resolveTagColor(tag.hexColor) }} />
                {tag.name}
              </span>
            );
          })}
        </div>
      )}

      {/* Bio */}
      {talent.bio && (
        <p className="text-sm text-tertiary line-clamp-2">{talent.bio}</p>
      )}
    </div>
  );
};